import { FormEvent, ReactNode, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import { toast } from "@/lib/toast";
import { getAuthRedirectUrl, getOAuthRedirectUrl } from "@/lib/authRedirect";
import { supabase } from "@/integrations/supabase/client";
import { MobileFrame } from "@/components/MobileFrame";
import { useTheme } from "@/context/ThemeContext";
import { THEMES, THEME_IDS, ThemeId } from "@/lib/themes";
import { INTERESTS, MAX_INTERESTS } from "@/lib/interests";
import { useFollow } from "@/context/FollowContext";
import { Swatch, type SwatchKind } from "@/components/primitives";
import { pickDefaultAvatar } from "@/lib/defaultAvatar";

// ─────────────────────────────────────────────────────────────────────────────
// FIG onboarding — 8 screens:
//   1 Splash → 2 Account → 3 Username → 4 Interests → 5 Follow →
//   6 Theme → 7 Help-improve → 8 CTA
//
// Profile data (theme, interests, training opt-in) is staged locally and
// written to the DB in one upsert when the user completes step 8. Follows
// are persisted live as the user toggles them in step 5 (FollowContext
// already mutates the follows table on toggle).
// ─────────────────────────────────────────────────────────────────────────────

type Step = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

const PROGRESS: Record<Step, number> = { 1: 0, 2: 14, 3: 28, 4: 42, 5: 56, 6: 70, 7: 84, 8: 100 };

interface Staged {
  themeId: ThemeId;
  interests: string[];
  allowTraining: boolean;
  consentAcknowledged: boolean;
}

export default function Onboard() {
  const nav = useNavigate();
  const [step, setStep] = useState<Step>(1);
  const [direction, setDirection] = useState<1 | -1>(1);
  const [staged, setStaged] = useState<Staged>({ themeId: "butter", interests: [], allowTraining: false, consentAcknowledged: false });
  const [sessionLoading, setSessionLoading] = useState(true);
  // True when the user arrived with an existing session (no account-creation step needed).
  const [hasExistingSession, setHasExistingSession] = useState(false);

  // If a session already exists when we mount (returning from Google OAuth or
  // email confirmation), skip account creation and go to profile setup.
  // Also redirect immediately if already onboarded.
  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        const uid = session.user.id;
        const [{ data: profile }, { data: adminRole }] = await Promise.all([
          supabase.from("profiles").select("onboarding_complete").eq("id", uid).maybeSingle(),
          supabase.from("user_roles").select("role").eq("user_id", uid).eq("role", "admin").maybeSingle(),
        ]);
        const alreadyDone = !!profile?.onboarding_complete;
        const isAdmin = !!adminRole;
        if (alreadyDone || isAdmin) {
          // Auto-fix: if admin slipped through without the flag, patch it now.
          if (isAdmin && !alreadyDone) {
            await supabase.from("profiles")
              .update({ onboarding_complete: true, walkthrough_complete: true } as never)
              .eq("id", uid);
          }
          nav("/home", { replace: true });
          return;
        }
        localStorage.removeItem("fig.oauth_onboarding");
        localStorage.removeItem("fig.signup_pending");
        setHasExistingSession(true);
        setStep(3);
      }
      setSessionLoading(false);
    });
  }, [nav]);

  const goto = (next: Step) => {
    setDirection(next > step ? 1 : -1);
    setStep(next);
  };

  if (sessionLoading) return null;

  return (
    <MobileFrame>
      <div style={{ minHeight: "100vh", overflow: "hidden", position: "relative" }}>
        <ScreenSwitcher key={step} direction={direction}>
          {step === 1 && (
            <Splash
              onAdvance={async () => {
                // "Get started" routes straight to /signup — no more
                // intermediate AccountStep ("Save your spot") page.
                // Sign out any stale session so /signup starts clean.
                try { await supabase.auth.signOut(); } catch { /* ignore */ }
                nav("/signup");
              }}
              onSignIn={async () => {
                try { await supabase.auth.signOut(); } catch { /* ignore */ }
                nav("/auth");
              }}
              onBrowse={() => nav("/feed")}
            />
          )}
          {step === 2 && (
            <AccountStep
              onBack={() => goto(1)}
              onAccountReady={() => goto(3)}
            />
          )}
          {step === 3 && (
            <UsernameStep
              onBack={() => goto(hasExistingSession ? 1 : 2)}
              onNext={() => goto(4)}
            />
          )}
          {step === 4 && (
            <InterestsStep
              staged={staged}
              setStaged={setStaged}
              onBack={() => goto(3)}
              onNext={() => goto(5)}
            />
          )}
          {step === 5 && (
            <FollowStep
              interests={staged.interests}
              onBack={() => goto(4)}
              onNext={() => goto(6)}
            />
          )}
          {step === 6 && (
            <ThemeStep
              staged={staged}
              setStaged={setStaged}
              onBack={() => goto(5)}
              onNext={() => goto(7)}
            />
          )}
          {step === 7 && (
            <HelpImproveStep
              allowTraining={staged.allowTraining}
              acknowledged={staged.consentAcknowledged}
              onToggleTraining={(v) => setStaged((s) => ({ ...s, allowTraining: v }))}
              onAcknowledge={(v) => setStaged((s) => ({ ...s, consentAcknowledged: v }))}
              onNavigate={(p) => nav(p)}
              onBack={() => goto(6)}
              onNext={() => goto(8)}
            />
          )}
          {step === 8 && (
            <CtaStep
              onChoose={async (path) => {
                const { data: u } = await supabase.auth.getUser();
                if (!u?.user) {
                  toast.error("Session expired — please sign in again.");
                  nav("/auth", { replace: true });
                  return;
                }

                // Resolve handle so the upsert never hits the NOT NULL constraint.
                const { data: existingProfile } = await supabase
                  .from("profiles").select("handle").eq("id", u.user.id).maybeSingle();
                const metaHandle = (u.user.user_metadata?.handle as string | undefined)
                  ?.toLowerCase().replace(/[^a-z0-9_]/g, "").substring(0, 30);
                const resolvedHandle =
                  (existingProfile?.handle && existingProfile.handle !== "")
                    ? existingProfile.handle
                    : metaHandle || (u.user.email ?? "").split("@")[0].replace(/[^a-z0-9_]/g, "").substring(0, 30) || "picker";

                const { error } = await supabase.from("profiles")
                  .upsert({
                    id: u.user.id,
                    handle: resolvedHandle,
                    display_name: resolvedHandle,
                    avatar: pickDefaultAvatar(u.user.id),
                    theme: staged.themeId,
                    interests: staged.interests,
                    tags: staged.interests,
                    allow_training_use: staged.allowTraining,
                    onboarding_complete: true,
                    // walkthrough_complete stays false so the first-time
                    // /home spotlight tour runs once. WalkthroughOverlay
                    // flips it to true when the user finishes or dismisses
                    // the tour.
                    walkthrough_complete: false,
                  } as never, { onConflict: "id" });
                if (error) {
                  console.error("profile upsert failed:", error);
                  toast.error(`Couldn't save your profile: ${error.message}`);
                  return;
                }
                nav(path, { replace: true });
              }}
            />
          )}
        </ScreenSwitcher>
      </div>
    </MobileFrame>
  );
}

// ── Shared chrome ──────────────────────────────────────────────────────────

function ScreenChrome({
  step, onBack, children,
}: { step: Step; onBack?: () => void; children: ReactNode }) {
  return (
    <div className="paper-bg" style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Progress bar */}
      <div style={{ height: 3, background: "hsl(var(--paper-2))", position: "relative" }}>
        <div style={{
          position: "absolute", left: 0, top: 0, bottom: 0,
          width: `${PROGRESS[step]}%`,
          background: "hsl(var(--rust))",
          transition: "width 350ms ease-out",
        }} />
      </div>
      {/* Back */}
      <div style={{ padding: "20px 20px 0", minHeight: 44 }}>
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            aria-label="Back"
            style={{
              background: "none", border: "none", padding: 6, marginLeft: -6,
              cursor: "pointer", color: "hsl(var(--ink))",
              display: "inline-flex", alignItems: "center", gap: 6,
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
              letterSpacing: "0.15em", textTransform: "uppercase",
            }}
          >
            <ArrowLeft size={16} /> Back
          </button>
        )}
      </div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "8px 24px 28px" }}>
        {children}
      </div>
    </div>
  );
}

function ScreenSwitcher({ children, direction }: { children: ReactNode; direction: 1 | -1 }) {
  return (
    <div
      style={{
        animation: `fig-slide-${direction === 1 ? "in-right" : "in-left"} 350ms ease-out`,
      }}
    >
      <style>{`
        @keyframes fig-slide-in-right {
          from { transform: translateX(24px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes fig-slide-in-left {
          from { transform: translateX(-24px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
      {children}
    </div>
  );
}

function Eyebrow({ children, withRule = false }: { children: ReactNode; withRule?: boolean }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
      {withRule && <div style={{ width: 22, height: 1, background: "hsl(var(--rust))" }} />}
      <span className="caps" style={{ color: "hsl(var(--rust))" }}>{children}</span>
    </div>
  );
}

function Display({ first, accent }: { first: string; accent: string }) {
  return (
    <h1 style={{
      fontFamily: "'Cormorant Garamond', 'Fraunces', 'Playfair Display', serif",
      fontWeight: 500, fontSize: 54, lineHeight: 0.98,
      color: "hsl(var(--ink))", letterSpacing: "-0.02em", margin: 0,
    }}>
      {first}<br />
      <span style={{ fontStyle: "italic", color: "hsl(var(--rust))" }}>{accent}</span>
    </h1>
  );
}

function Body({ children }: { children: ReactNode }) {
  return (
    <p style={{
      fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 16, lineHeight: 1.55,
      color: "hsl(var(--ink-2))", margin: "16px 0 0",
    }}>{children}</p>
  );
}

function PrimaryButton({ children, onClick, type = "button", disabled, busy }: {
  children: ReactNode; onClick?: () => void; type?: "button" | "submit"; disabled?: boolean; busy?: boolean;
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || busy}
      style={{
        width: "100%", padding: "16px 18px",
        background: "hsl(var(--ink))", color: "hsl(var(--ink-fg))",
        border: "1px solid hsl(var(--ink))",
        fontFamily: "'IBM Plex Mono', monospace", fontSize: 12,
        letterSpacing: "0.18em", textTransform: "uppercase",
        cursor: busy ? "wait" : disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1,
      }}
    >
      {busy ? "…" : children}
    </button>
  );
}

function SkipLink({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: "none", border: "none", padding: "10px 0",
        cursor: "pointer", width: "100%",
        fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
        letterSpacing: "0.15em", textTransform: "uppercase",
        color: "hsl(var(--ink-2))",
      }}
    >
      {children}
    </button>
  );
}

// ─── Screen 1 — Splash ─────────────────────────────────────────────────────

function Splash({
  onAdvance, onSignIn, onBrowse,
}: { onAdvance: () => void; onSignIn: () => void; onBrowse: () => void }) {
  return (
    <div
      className="paper-bg"
      style={{
        minHeight: "100vh",
        display: "flex", flexDirection: "column",
        padding: "60px 28px 36px",
      }}
    >
      <div style={{ flex: 1, display: "flex", flexDirection: "column",
                    alignItems: "center", justifyContent: "center", gap: 18 }}>
        <div style={{
          fontFamily: "'Fraunces', 'Playfair Display', serif",
          fontWeight: 900, fontStyle: "italic", fontSize: 160,
          letterSpacing: "-0.06em", lineHeight: 0.85, color: "hsl(var(--ink))",
          fontVariationSettings: '"opsz" 144, "SOFT" 100, "WONK" 1',
        }}>fig</div>
        <div style={{
          fontFamily: "'Fraunces', serif",
          fontStyle: "italic", fontWeight: 600, fontSize: 26,
          color: "hsl(var(--rust))",
          fontVariationSettings: '"opsz" 144, "SOFT" 100',
          marginTop: 4,
        }}>Know your closet.</div>
        <p style={{
          marginTop: 6, maxWidth: 280, textAlign: "center",
          fontFamily: "'Fraunces', 'Playfair Display', serif",
          fontSize: 14, lineHeight: 1.5, color: "hsl(var(--ink-2))",
        }}>
          The vintage-first home for finds, closets, and the people who love them.
        </p>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <PrimaryButton onClick={onAdvance}>Get started</PrimaryButton>
        <button
          type="button"
          onClick={onSignIn}
          style={{
            width: "100%", padding: "16px 18px",
            background: "transparent", color: "hsl(var(--ink))",
            border: "1px solid hsl(var(--ink))",
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 12,
            letterSpacing: "0.18em", textTransform: "uppercase",
            cursor: "pointer",
          }}
        >
          I have an account
        </button>
        <div style={{
          marginTop: 6, textAlign: "center",
          fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 13,
          color: "hsl(var(--ink-2))",
        }}>
          Just looking?{" "}
          <button
            type="button"
            onClick={onBrowse}
            style={{
              background: "none", border: "none", padding: 0, cursor: "pointer",
              color: "hsl(var(--rust))", fontFamily: "inherit", fontSize: "inherit",
              fontWeight: 700, textDecoration: "underline",
            }}
          >
            Browse fig →
          </button>
        </div>
        {/* Privacy + Terms links. Required on the home page for Google
            OAuth verification (Google rejected the verification attempt
            because /  didn't include a link to the privacy policy). */}
        <div style={{
          marginTop: 14, textAlign: "center",
          fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
          letterSpacing: "0.14em", textTransform: "uppercase",
          color: "hsl(var(--ink-2))",
          display: "flex", gap: 14, justifyContent: "center",
        }}>
          <a href="/privacy" style={{ color: "hsl(var(--ink-2))", textDecoration: "underline" }}>
            Privacy
          </a>
          <a href="/terms" style={{ color: "hsl(var(--ink-2))", textDecoration: "underline" }}>
            Terms
          </a>
        </div>
      </div>
    </div>
  );
}

// ─── Screen 2 — Account creation ───────────────────────────────────────────

function AccountStep({
  onBack, onAccountReady,
}: { onBack: () => void; onAccountReady: () => void }) {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [resending, setResending] = useState(false);

  // If a session already exists (e.g. user pressed back from avatar step),
  // skip account creation immediately.
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) onAccountReady();
    });
  }, [onAccountReady]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFieldError(null);
    setBusy(true);
    try {
      try { await supabase.auth.signOut(); } catch { /* ignore */ }
      // Verification-email signup. Password is a throwaway here —
      // UsernameStep replaces it with the user's real one via
      // updateUser after they click the verification link.
      const tempPassword = crypto.randomUUID() + crypto.randomUUID();
      const { error } = await supabase.auth.signUp({
        email,
        password: tempPassword,
        options: { emailRedirectTo: getAuthRedirectUrl("/") },
      });
      if (error) {
        if (/already registered|already exists/i.test(error.message)) {
          setFieldError("That email is already registered. Try signing in instead.");
        } else if (error.message.toLowerCase().includes("invalid email")) {
          setFieldError("Please enter a valid email address.");
        } else {
          setFieldError(error.message);
        }
        return;
      }
      localStorage.setItem("fig.signup_pending", "1");
      setPendingEmail(email);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Something went wrong";
      setFieldError(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    if (!pendingEmail) return;
    setResending(true);
    const { error } = await supabase.auth.resend({
      type: "signup",
      email: pendingEmail,
      options: { emailRedirectTo: getAuthRedirectUrl("/") },
    });
    setResending(false);
    if (error) toast.error(error.message);
    else toast.success("Verification email resent.");
  };

  const handleOAuth = async () => {
    localStorage.setItem("fig.oauth_onboarding", "1");
    setBusy(true);
    try { await supabase.auth.signOut(); } catch { /* ignore */ }
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: getOAuthRedirectUrl("/auth/callback") },
    });
    if (error) {
      toast.error(error.message ?? "Google sign-in failed");
      setBusy(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "12px 14px",
    border: "1px solid hsl(var(--ink))",
    background: "hsl(var(--paper))",
    fontFamily: "'IBM Plex Mono', monospace", fontSize: 13,
    color: "hsl(var(--ink))", outline: "none",
  };

  if (pendingEmail) {
    return (
      <ScreenChrome step={2} onBack={() => setPendingEmail(null)}>
        <Eyebrow>01 · Join</Eyebrow>
        <h1 style={{
          fontFamily: "'Cormorant Garamond', 'Fraunces', 'Playfair Display', serif",
          fontWeight: 500, fontSize: 48, lineHeight: 1.0,
          color: "hsl(var(--ink))", letterSpacing: "-0.02em", margin: 0,
          fontStyle: "italic",
        }}>Check your inbox.</h1>
        <p style={{
          marginTop: 12,
          fontFamily: "'Fraunces', 'Playfair Display', serif",
          fontSize: 15, color: "hsl(var(--ink-2))", lineHeight: 1.5,
        }}>
          We sent a verification link to <strong>{pendingEmail}</strong>. Click it to activate your account, then return here.
        </p>
        <div style={{ flex: 1 }} />
        <PrimaryButton onClick={handleResend} busy={resending}>
          Resend verification email →
        </PrimaryButton>
      </ScreenChrome>
    );
  }

  return (
    <ScreenChrome step={2} onBack={onBack}>
      <Eyebrow>01 · Join</Eyebrow>
      <h1 style={{
        fontFamily: "'Cormorant Garamond', 'Fraunces', 'Playfair Display', serif",
        fontWeight: 500, fontSize: 48, lineHeight: 1.0,
        color: "hsl(var(--ink))", letterSpacing: "-0.02em", margin: 0,
        fontStyle: "italic",
      }}>Save your spot.</h1>
      <p style={{
        marginTop: 10,
        fontFamily: "'Fraunces', 'Playfair Display', serif",
        fontSize: 14, color: "hsl(var(--ink-2))", lineHeight: 1.5, margin: "10px 0 0",
      }}>Takes about 30 seconds. Promise.</p>

      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 22 }}>
        <button type="button" onClick={handleOAuth} disabled={busy} style={oauthStyle}>
          Continue with Google
        </button>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "20px 0 12px" }}>
        <div style={{ flex: 1, borderTop: "1px dashed hsl(var(--sand-deep))" }} />
        <span className="caps" style={{ color: "hsl(var(--ink-2))" }}>or</span>
        <div style={{ flex: 1, borderTop: "1px dashed hsl(var(--sand-deep))" }} />
      </div>

      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div>
          <label htmlFor="onb-email" className="caps" style={{ display: "block", marginBottom: 6 }}>Email</label>
          <input id="onb-email" type="email" required autoComplete="email"
            placeholder="your@email.com" value={email} onChange={(e) => { setEmail(e.target.value); setFieldError(null); }}
            style={inputStyle} />
          <p style={{ marginTop: 8, fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: "hsl(var(--ink-2))", lineHeight: 1.4 }}>
            We'll send a verification email. Click the link inside to confirm — you'll pick a username and password on the next screen.
          </p>
        </div>

        {fieldError && (
          <p style={{ margin: 0, fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: "hsl(var(--rust))", lineHeight: 1.4 }}>
            {fieldError}
          </p>
        )}

        <div style={{ marginTop: 8 }}>
          <button type="submit" disabled={busy}
            style={{
              width: "100%", padding: "16px 18px",
              background: "hsl(var(--rust))", color: "hsl(var(--rust-fg))",
              border: "1px solid hsl(var(--rust))",
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 12,
              letterSpacing: "0.18em", textTransform: "uppercase",
              cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1,
            }}>
            {busy ? "…" : "Create account"}
          </button>
        </div>
      </form>

      <div style={{
        marginTop: 18, textAlign: "center",
        fontFamily: "'IBM Plex Mono', monospace", fontSize: 9,
        letterSpacing: "0.1em", color: "hsl(var(--ink-2))", lineHeight: 1.5,
      }}>
        BY JOINING YOU AGREE TO FIG'S TERMS AND PRIVACY POLICY. NO FAST FASHION. EVER.
      </div>

      <div style={{ textAlign: "center", marginTop: 14 }}>
        <span style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 13, color: "hsl(var(--ink-2))" }}>
          Already with us?{" "}
          <button
            type="button"
            onClick={async () => {
              try { await supabase.auth.signOut(); } catch { /* ignore */ }
              nav("/auth");
            }}
            style={{
              background: "none", border: "none", padding: 0, cursor: "pointer",
              color: "hsl(var(--rust))", fontFamily: "inherit", fontSize: "inherit",
              textDecoration: "underline",
            }}
          >
            Sign in
          </button>
        </span>
      </div>
    </ScreenChrome>
  );
}

const oauthStyle: React.CSSProperties = {
  width: "100%", padding: "12px 16px",
  background: "hsl(var(--cream))", color: "hsl(var(--ink))",
  border: "1px solid hsl(var(--ink))",
  fontFamily: "'IBM Plex Mono', monospace", fontSize: 12,
  letterSpacing: "0.18em", textTransform: "uppercase", cursor: "pointer",
};

// ─── Screen 3 — Username ───────────────────────────────────────────────────

const HANDLE_RE = /^[a-z0-9_]{3,20}$/;

function UsernameStep({ onBack, onNext }: { onBack: () => void; onNext: () => void }) {
  const [handle, setHandle] = useState("");
  const [status, setStatus] = useState<"idle" | "checking" | "available" | "taken" | "invalid">("idle");
  const [busy, setBusy] = useState(false);
  // Password fields are shown ONLY for email-signup users (they
  // arrived via the verification-email flow that set a throwaway
  // password — we need them to pick a real one here). Apple guideline
  // 4 explicitly forbids requiring a password after Sign in with
  // Apple, so OAuth signups (provider 'apple' or 'google') skip the
  // password step entirely.
  const [needsPassword, setNeedsPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const checkTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Pre-fill the handle from whatever the trigger set on the profile row,
  // and decide whether to render password fields based on the user's
  // auth identities. Only users with an 'email' provider identity see
  // the password fields — Apple / Google signups skip them.
  useEffect(() => {
    supabase.auth.getUser().then(async ({ data: { user } }) => {
      if (!user) return;
      const { data } = await supabase
        .from("profiles").select("handle").eq("id", user.id).maybeSingle();
      if (data?.handle) setHandle(data.handle);
      const identities = (user.identities ?? []) as Array<{ provider?: string }>;
      const hasEmailIdentity = identities.some((i) => i.provider === "email");
      setNeedsPassword(hasEmailIdentity);
    });
  }, []);

  const checkHandle = (raw: string) => {
    const val = raw.toLowerCase().replace(/[^a-z0-9_]/g, "");
    setHandle(val);
    if (checkTimer.current) clearTimeout(checkTimer.current);
    if (!val) { setStatus("idle"); return; }
    if (!HANDLE_RE.test(val)) { setStatus("invalid"); return; }
    setStatus("checking");
    checkTimer.current = setTimeout(async () => {
      const { data: { user } } = await supabase.auth.getUser();
      const { data } = await supabase
        .from("profiles").select("id").eq("handle", val)
        .neq("id", user?.id ?? "").maybeSingle();
      setStatus(data ? "taken" : "available");
    }, 350);
  };

  const passwordsMatch = password === confirmPassword;
  const passwordLongEnough = password.length >= 8;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (status === "taken" || status === "invalid" || status === "checking") return;
    if (needsPassword) {
      if (!passwordLongEnough) {
        toast.error("Password must be at least 8 characters.");
        return;
      }
      if (!passwordsMatch) {
        toast.error("Passwords don't match.");
        return;
      }
    }
    setBusy(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { error } = await supabase.from("profiles")
        .upsert({ id: user.id, handle, display_name: handle } as never, { onConflict: "id" });
      if (error) { toast.error(`Couldn't save username: ${error.message}`); setBusy(false); return; }
      if (needsPassword) {
        // Replace the throwaway password the verification flow set with
        // the user's real one. Skipped entirely for Apple / Google
        // signups — Apple guideline 4 forbids password setup after
        // Sign in with Apple, and they authenticate via the OAuth
        // identity going forward.
        const { error: pwErr } = await supabase.auth.updateUser({ password });
        if (pwErr) { toast.error(`Couldn't set password: ${pwErr.message}`); setBusy(false); return; }
      }
    }
    setBusy(false);
    onNext();
  };

  const statusColor = status === "available" ? "hsl(var(--olive))"
    : status === "taken" || status === "invalid" ? "hsl(var(--rust))"
    : "hsl(var(--ink-2))";
  const statusMsg = status === "available" ? "✓ Available"
    : status === "taken" ? "✗ Already taken"
    : status === "invalid" ? "3–20 characters, letters, numbers and _ only"
    : status === "checking" ? "Checking…" : "";

  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "12px 14px",
    border: `1px solid ${status === "taken" || status === "invalid" ? "hsl(var(--rust))" : status === "available" ? "hsl(var(--olive))" : "hsl(var(--ink))"}`,
    background: "hsl(var(--paper))",
    fontFamily: "'IBM Plex Mono', monospace", fontSize: 15,
    color: "hsl(var(--ink))", outline: "none", boxSizing: "border-box",
  };

  const canSubmit =
    handle.length >= 3 &&
    (status === "available" || status === "idle") &&
    (!needsPassword || (passwordLongEnough && passwordsMatch));

  return (
    <ScreenChrome step={3} onBack={onBack}>
      <Eyebrow>02 · Username</Eyebrow>
      <h1 style={{
        fontFamily: "'Cormorant Garamond', 'Fraunces', 'Playfair Display', serif",
        fontWeight: 500, fontSize: 48, lineHeight: 1.0,
        color: "hsl(var(--ink))", letterSpacing: "-0.02em", margin: 0, fontStyle: "italic",
      }}>Pick your handle.</h1>
      <p style={{
        marginTop: 10,
        fontFamily: "'Fraunces', 'Playfair Display', serif",
        fontSize: 14, color: "hsl(var(--ink-2))", lineHeight: 1.5, margin: "10px 0 0",
      }}>This is how people find you in The Archive.</p>

      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 28 }}>
        <div>
          <label htmlFor="onb-handle" className="caps" style={{ display: "block", marginBottom: 6 }}>Username</label>
          <div style={{ position: "relative" }}>
            <span style={{
              position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)",
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 15,
              color: "hsl(var(--ink-2))", pointerEvents: "none",
            }}>@</span>
            <input
              id="onb-handle"
              type="text"
              required
              autoComplete="username"
              placeholder="yourname"
              value={handle}
              onChange={(e) => checkHandle(e.target.value)}
              style={{ ...inputStyle, paddingLeft: 28 }}
            />
          </div>
          {statusMsg && (
            <div style={{
              marginTop: 6, fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 11, color: statusColor,
            }}>{statusMsg}</div>
          )}
        </div>

        {needsPassword && (
          <>
            <div>
              <label htmlFor="onb-password" className="caps" style={{ display: "block", marginBottom: 6, marginTop: 6 }}>
                Password
              </label>
              <div style={{ position: "relative" }}>
                <input
                  id="onb-password"
                  type={showPassword ? "text" : "password"}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  placeholder="At least 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{ ...inputStyle, paddingRight: 40 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((p) => !p)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  style={{
                    position: "absolute", right: 10, top: 0, bottom: 0,
                    background: "none", border: "none",
                    color: "hsl(var(--ink-2))", cursor: "pointer",
                    display: "flex", alignItems: "center",
                  }}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="onb-confirm-password" className="caps" style={{ display: "block", marginBottom: 6, marginTop: 6 }}>
                Confirm password
              </label>
              <input
                id="onb-confirm-password"
                type={showPassword ? "text" : "password"}
                required
                minLength={8}
                autoComplete="new-password"
                placeholder="Repeat your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                style={{
                  ...inputStyle,
                  borderColor: confirmPassword && !passwordsMatch
                    ? "hsl(var(--rust))"
                    : (inputStyle.border as string).split(" ").pop() ?? "hsl(var(--ink))",
                }}
              />
              {confirmPassword && !passwordsMatch && (
                <div style={{
                  marginTop: 6, fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 11, color: "hsl(var(--rust))",
                }}>
                  ✗ Passwords don't match
                </div>
              )}
              {confirmPassword && passwordsMatch && passwordLongEnough && (
                <div style={{
                  marginTop: 6, fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 11, color: "hsl(var(--olive))",
                }}>
                  ✓ Passwords match
                </div>
              )}
            </div>
          </>
        )}

        <div style={{ flex: 1, minHeight: 24 }} />

        <PrimaryButton type="submit" busy={busy} disabled={!canSubmit}>
          Continue →
        </PrimaryButton>
      </form>
    </ScreenChrome>
  );
}

// ─── Screen 4 — Theme ──────────────────────────────────────────────────────

function ThemeStep({
  staged, setStaged, onBack, onNext,
}: { staged: Staged; setStaged: React.Dispatch<React.SetStateAction<Staged>>; onBack: () => void; onNext: () => void }) {
  const { theme, setTheme } = useTheme();
  const pick = async (id: ThemeId) => {
    setStaged((prev) => ({ ...prev, themeId: id }));
    await setTheme(id);
  };
  useEffect(() => {
    setStaged((prev) => prev.themeId !== theme ? { ...prev, themeId: theme as ThemeId } : prev);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ScreenChrome step={6} onBack={onBack}>
      <Eyebrow>Make FIG yours</Eyebrow>
      <Display first="Pick your" accent="palette." />
      <Body>Choose the look and feel that's right for you. You can change this anytime in settings.</Body>

      <div style={{
        marginTop: 22,
        display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10,
      }}>
        {THEME_IDS.map((id) => {
          const meta = THEMES[id];
          const active = staged.themeId === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => pick(id)}
              aria-pressed={active}
              style={{
                textAlign: "left",
                background: "hsl(var(--cream))",
                border: active ? "2px solid hsl(var(--rust))" : "1px solid hsl(var(--ink))",
                padding: 10, cursor: "pointer",
                display: "flex", flexDirection: "column", gap: 8,
              }}
            >
              <div style={{ display: "flex", gap: 4 }}>
                {meta.preview.map((hsl, i) => (
                  <div key={i} style={{
                    flex: 1, height: 22,
                    background: `hsl(${hsl})`, border: "1px solid hsl(var(--ink))",
                  }} />
                ))}
              </div>
              <div>
                <div style={{
                  fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 14,
                  color: "hsl(var(--ink))", lineHeight: 1.1,
                }}>{meta.name}</div>
                <div style={{
                  fontFamily: "'IBM Plex Mono', monospace", fontSize: 9,
                  letterSpacing: "0.06em", color: "hsl(var(--ink-2))",
                  marginTop: 4, lineHeight: 1.3,
                }}>{meta.tagline}</div>
              </div>
            </button>
          );
        })}
      </div>

      <div style={{ flex: 1 }} />
      <div style={{ marginTop: 24 }}>
        <PrimaryButton onClick={onNext}>This is my vibe →</PrimaryButton>
      </div>
    </ScreenChrome>
  );
}

// ─── Screen 5 — Interests ──────────────────────────────────────────────────

function InterestsStep({
  staged, setStaged, onBack, onNext,
}: { staged: Staged; setStaged: React.Dispatch<React.SetStateAction<Staged>>; onBack: () => void; onNext: () => void }) {
  const groups = ["Categories", "Eras", "Aesthetics", "Regions"] as const;
  const toggle = (value: string) => {
    setStaged((prev) => {
      const cur = prev.interests;
      if (cur.includes(value)) {
        return { ...prev, interests: cur.filter((v) => v !== value) };
      } else if (cur.length < MAX_INTERESTS) {
        return { ...prev, interests: [...cur, value] };
      }
      return prev;
    });
  };

  return (
    <ScreenChrome step={4} onBack={onBack}>
      <Eyebrow>Your taste</Eyebrow>
      <Display first="What do you" accent="love?" />
      <Body>We'll use this to personalise your feed. Choose up to ten!</Body>

      <div style={{
        marginTop: 18, display: "flex", flexDirection: "column", gap: 14,
        maxHeight: "46vh", overflowY: "auto", paddingRight: 4,
      }}>
        {groups.map((group) => (
          <div key={group}>
            <div className="caps" style={{ marginBottom: 6, color: "hsl(var(--ink-2))" }}>{group}</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {INTERESTS.filter((i) => i.group === group).map((interest) => {
                const sel = staged.interests.includes(interest.value);
                const cap = !sel && staged.interests.length >= MAX_INTERESTS;
                return (
                  <button
                    key={interest.value}
                    type="button"
                    onClick={() => !cap && toggle(interest.value)}
                    disabled={cap}
                    aria-pressed={sel}
                    style={{
                      padding: "8px 12px",
                      background: sel ? "hsl(var(--ink))" : "hsl(var(--cream))",
                      color: sel ? "hsl(var(--ink-fg))" : "hsl(var(--ink))",
                      border: "1px solid hsl(var(--ink))",
                      fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
                      letterSpacing: "0.1em", textTransform: "uppercase",
                      cursor: cap ? "not-allowed" : "pointer",
                      opacity: cap ? 0.4 : 1,
                    }}
                  >
                    {sel && "✓ "}{interest.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="caps" style={{ marginTop: 12, color: "hsl(var(--ink-2))" }}>
        You can update your interests anytime from your profile.
      </div>

      <div style={{ flex: 1 }} />
      <div style={{ marginTop: 18 }}>
        <PrimaryButton onClick={onNext}>Almost there →</PrimaryButton>
        <SkipLink onClick={onNext}>Skip for now</SkipLink>
      </div>
    </ScreenChrome>
  );
}

// ─── Screen 5 — Suggested follows ──────────────────────────────────────────
// Builds a 20-row pool of public collectors. Rows are scored by interest-
// tag overlap with the user's just-picked tags (client-side; the pool is
// small enough that pushing array-overlap to SQL isn't worth it). Matched
// rows come first, sorted by overlap; if fewer than 20 matched profiles
// exist, fill the tail with featured/recently-active public profiles. The
// step is fully skippable — no rows required to advance.

interface FollowSuggestion {
  id: string;
  handle: string;
  display_name: string | null;
  avatar: string;
  tags: string[];
  overlap: number;
}

function FollowStep({
  interests, onBack, onNext,
}: { interests: string[]; onBack: () => void; onNext: () => void }) {
  const { isFollowing, toggle } = useFollow();
  const [rows, setRows] = useState<FollowSuggestion[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      const myId = auth.user?.id ?? null;
      if (!myId) {
        if (!cancelled) { setLoading(false); }
        return;
      }
      const { data } = await (supabase as unknown as {
        from: (t: string) => {
          select: (cols: string) => {
            neq: (col: string, val: string) => {
              eq: (col: string, val: boolean) => {
                limit: (n: number) => Promise<{ data: unknown }>;
              };
            };
          };
        };
      })
        .from("profiles_public")
        .select("id, handle, display_name, avatar, tags")
        .neq("id", myId)
        .eq("is_private", false)
        .limit(200);
      if (cancelled) return;
      const all: FollowSuggestion[] = ((data ?? []) as Array<{
        id: string; handle: string; display_name: string | null;
        avatar: string; tags: string[] | null;
      }>)
        .filter((p) => !!p.handle)
        .map((p) => ({
          id: p.id,
          handle: p.handle,
          display_name: p.display_name,
          avatar: p.avatar ?? "olive",
          tags: Array.isArray(p.tags) ? p.tags : [],
          overlap: 0,
        }));
      const mySet = new Set(interests);
      for (const r of all) {
        let o = 0;
        for (const t of r.tags) if (mySet.has(t)) o++;
        r.overlap = o;
      }
      const matched = all
        .filter((r) => r.overlap > 0)
        .sort((a, b) => b.overlap - a.overlap);
      let featured: FollowSuggestion[] = [];
      if (matched.length < 20) {
        const matchedIds = new Set(matched.map((r) => r.id));
        featured = all
          .filter((r) => !matchedIds.has(r.id))
          .slice(0, 20 - matched.length);
      }
      const combined = [...matched, ...featured].slice(0, 20);
      if (!cancelled) {
        setRows(combined);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [interests]);

  return (
    <ScreenChrome step={5} onBack={onBack}>
      <Eyebrow>Your feed</Eyebrow>
      <Display first="Some collectors" accent="you'd vibe with." />
      <Body>Tap Follow to add anyone to your feed. Totally optional — you can always tweak this later.</Body>

      <div style={{
        marginTop: 14, display: "flex", flexDirection: "column", gap: 6,
        maxHeight: "50vh", overflowY: "auto", paddingRight: 4,
      }}>
        {loading ? (
          <div style={{
            padding: "32px 12px", textAlign: "center",
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
            letterSpacing: "0.15em", color: "hsl(var(--ink-2))",
          }}>
            FINDING COLLECTORS…
          </div>
        ) : rows.length === 0 ? (
          <div style={{
            padding: "16px 12px", textAlign: "center",
            fontFamily: "'Fraunces', 'Playfair Display', serif",
            fontSize: 13, lineHeight: 1.4, color: "hsl(var(--ink-2))",
          }}>
            No collectors to suggest yet — Fig is still early. You'll be one of the first.
          </div>
        ) : (
          rows.map((r) => {
            const following = isFollowing(r.id);
            return (
              <div
                key={r.id}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "8px 10px",
                  border: "1px solid hsl(var(--ink-2))",
                  background: "hsl(var(--cream))",
                }}
              >
                <Swatch
                  kind={(r.avatar as SwatchKind) || "olive"}
                  style={{ width: 34, height: 34, borderRadius: "50%", border: "1px solid hsl(var(--ink))", flexShrink: 0 }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 13,
                    color: "hsl(var(--ink))",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    {r.display_name || `@${r.handle}`}
                  </div>
                  <div className="caps" style={{
                    color: "hsl(var(--ink-2))", fontSize: 9, marginTop: 1,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    @{r.handle}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => toggle(r.id)}
                  style={{
                    flexShrink: 0,
                    padding: "4px 10px",
                    background: following ? "transparent" : "hsl(var(--ink))",
                    color: following ? "hsl(var(--ink))" : "hsl(var(--ink-fg))",
                    border: "1px solid hsl(var(--ink))",
                    fontFamily: "'IBM Plex Mono', monospace", fontSize: 9,
                    letterSpacing: "0.1em", textTransform: "uppercase",
                    cursor: "pointer",
                  }}
                >
                  {following ? "Following" : "Follow"}
                </button>
              </div>
            );
          })
        )}
      </div>

      <div style={{ flex: 1 }} />
      <div style={{ marginTop: 18 }}>
        <PrimaryButton onClick={onNext}>Continue →</PrimaryButton>
        <SkipLink onClick={onNext}>Skip for now</SkipLink>
      </div>
    </ScreenChrome>
  );
}

// ─── Screen 7 — Help improve Fig ───────────────────────────────────────────
// Two checkboxes: training opt-in (optional) and ToS/Privacy acknowledgement
// (gating). Persisted to profiles.allow_training_use in the final upsert.

function HelpImproveStep({
  allowTraining, acknowledged, onToggleTraining, onAcknowledge, onNavigate, onBack, onNext,
}: {
  allowTraining: boolean;
  acknowledged: boolean;
  onToggleTraining: (v: boolean) => void;
  onAcknowledge: (v: boolean) => void;
  onNavigate: (path: string) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  return (
    <ScreenChrome step={7} onBack={onBack}>
      <Eyebrow>One last thing</Eyebrow>
      <Display first="Help Fig" accent="get smarter." />
      <Body>Your scans, photos, and verdicts are what teach the engine. You can change this anytime from Settings → Privacy.</Body>

      <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{
          padding: 14,
          border: "1px solid hsl(var(--ink))",
          background: "hsl(var(--cream))",
          display: "flex", gap: 10, alignItems: "flex-start",
        }}>
          <input
            type="checkbox"
            id="onboard-allow-training"
            checked={allowTraining}
            onChange={(e) => onToggleTraining(e.target.checked)}
            style={{ marginTop: 3, accentColor: "hsl(var(--rust))", flexShrink: 0 }}
          />
          <label htmlFor="onboard-allow-training" style={{ flex: 1, cursor: "pointer" }}>
            <div style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 14,
              color: "hsl(var(--ink))",
            }}>
              Help improve Fig
            </div>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
              color: "hsl(var(--ink-2))", lineHeight: 1.4, marginTop: 4,
            }}>
              Let Fig use your scans, photos, and verdicts to train future versions of the engine. Optional.
            </div>
          </label>
        </div>

        <div style={{
          padding: 14,
          border: "1px solid hsl(var(--ink))",
          background: "hsl(var(--cream))",
          display: "flex", gap: 10, alignItems: "flex-start",
        }}>
          <input
            type="checkbox"
            id="onboard-acknowledge-tos"
            checked={acknowledged}
            onChange={(e) => onAcknowledge(e.target.checked)}
            style={{ marginTop: 3, accentColor: "hsl(var(--rust))", flexShrink: 0 }}
          />
          <label htmlFor="onboard-acknowledge-tos" style={{ flex: 1, cursor: "pointer" }}>
            <div style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 13,
              color: "hsl(var(--ink))", lineHeight: 1.5,
            }}>
              I've read and agree to Fig's{" "}
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onNavigate("/terms"); }}
                style={{
                  background: "transparent", border: "none", padding: 0,
                  color: "hsl(var(--rust))", textDecoration: "underline",
                  fontFamily: "inherit", fontSize: "inherit", cursor: "pointer",
                }}
              >
                Terms of Service
              </button>
              {" "}and{" "}
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onNavigate("/privacy"); }}
                style={{
                  background: "transparent", border: "none", padding: 0,
                  color: "hsl(var(--rust))", textDecoration: "underline",
                  fontFamily: "inherit", fontSize: "inherit", cursor: "pointer",
                }}
              >
                Privacy Policy
              </button>
              .
            </div>
          </label>
        </div>
      </div>

      <div style={{ flex: 1 }} />
      <div style={{ marginTop: 18 }}>
        <PrimaryButton onClick={onNext} disabled={!acknowledged}>
          {acknowledged ? "Continue →" : "Tick the checkbox to continue"}
        </PrimaryButton>
      </div>
    </ScreenChrome>
  );
}

// ─── Screen 8 — CTA ────────────────────────────────────────────────────────

function CtaStep({ onChoose }: { onChoose: (path: string) => void }) {
  const [progressVisible, setProgressVisible] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setProgressVisible(false), 600);
    return () => clearTimeout(t);
  }, []);

  const cards: { short: string; icon: string; title: string; desc: string; path: string }[] = [
    { short: "Verify", icon: "📷", title: "Scan your first garment",
      desc: "Try FIG Verify free — upload photos of a piece and get a full authentication report.",
      path: "/scan" },
    { short: "Share", icon: "✦", title: "Follow someone with your taste",
      desc: "Find collectors and sellers who love what you love. Your feed gets better the more you follow.",
      path: "/leaders" },
    { short: "Discover", icon: "⌂", title: "Check out the feed",
      desc: "See what the FIG community is sharing right now. All vintage, no noise.",
      path: "/feed" },
  ];

  return (
    <div style={{
      minHeight: "100vh", display: "flex", flexDirection: "column",
      background: "hsl(var(--rust))", color: "hsl(var(--rust-fg))",
      padding: "60px 28px 36px",
    }}>
      <div className="caps" style={{
        color: "hsl(var(--rust-fg))", opacity: 0.8,
        letterSpacing: "0.22em", fontSize: 11, marginBottom: 24,
      }}>
        You're in
      </div>

      <div style={{
        flex: 1, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", gap: 14, textAlign: "center",
      }}>
        <div style={{
          fontFamily: "'Fraunces', 'Playfair Display', serif",
          fontWeight: 900, fontStyle: "italic", fontSize: 130,
          letterSpacing: "-0.06em", lineHeight: 0.85, color: "hsl(var(--rust-fg))",
          fontVariationSettings: '"opsz" 144, "SOFT" 100, "WONK" 1',
        }}>fig</div>
        <div style={{
          fontFamily: "'Fraunces', 'Playfair Display', serif",
          fontStyle: "italic", fontWeight: 700, fontSize: 30,
          color: "hsl(var(--rust-fg))",
        }}>Welcome to fig.</div>
        <p style={{
          marginTop: 4, maxWidth: 290,
          fontFamily: "'Fraunces', 'Playfair Display', serif",
          fontSize: 14, lineHeight: 1.5, color: "hsl(var(--rust-fg))", opacity: 0.9,
        }}>
          Your closet is open. The community is gathered. Let's see what's been hiding in everyone's racks.
        </p>

        <div style={{ display: "flex", gap: 8, marginTop: 18, flexWrap: "wrap", justifyContent: "center" }}>
          {cards.map((c) => (
            <button
              key={c.path}
              type="button"
              onClick={() => onChoose(c.path)}
              style={{
                padding: "8px 16px",
                background: "hsl(var(--rust-fg) / 0.12)",
                border: "1px solid hsl(var(--rust-fg) / 0.4)",
                color: "hsl(var(--rust-fg))",
                fontFamily: "'Fraunces', 'Playfair Display', serif",
                fontStyle: "italic", fontSize: 14, fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {c.short}
            </button>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={() => onChoose("/home")}
        style={{
          width: "100%", padding: "16px 18px",
          background: "hsl(var(--cream))", color: "hsl(var(--ink))",
          border: "1px solid hsl(var(--cream))",
          fontFamily: "'IBM Plex Mono', monospace", fontSize: 12,
          letterSpacing: "0.18em", textTransform: "uppercase",
          cursor: "pointer",
        }}
      >
        Take me into FIG →
      </button>
      <span style={{ display: "none" }}>{progressVisible ? "" : ""}</span>
    </div>
  );
}
